import { FC } from 'react';
import { SelfServiceAPI } from 'types';
import { useRoutableModal } from 'lib/hooks';
import ChangeDatesButton from './ChangeDatesButton';
import AddToCalendar from './AddToCalendar';
import Occupants from 'components/Occupants';
import ClassicIcon from 'components/ClassicIcon';
import RoomAndOfferModal from 'components/Modals/RoomAndOfferModal';
import GridData from 'components/GridData';
import { Link } from 'components/Typography';
import { Box, Text } from '@qga/roo-ui/components';
import * as track from 'lib/gaTracking/bookingDetailsPageEvents';
import { FLAG_APPLE_WALLET_PASSES_ENABLED } from 'config/env';
import DownloadAppleWalletPassButton from './DownloadAppleWalletPassButton';

const BookingDetailsCard: FC<{ booking: SelfServiceAPI.Booking }> = ({ booking }) => {
  const { RoutableModalWrapper, ...modal } = useRoutableModal('room-and-offer-information', {
    onCloseIteraction: track.roomInformationPopUpCloseButtonSelected,
  });

  const { reservation, reference, flags } = booking;
  const displayRoomType = reservation.roomType.name !== reservation.offer.name;
  const displayBedGroup = !!reservation.bedGroupDescription && reservation.bedGroupDescription !== '';

  const canDisplayAppleWalletPass = (flags: SelfServiceAPI.BookingFlags) => {
    const iOSChromeOrFirefox = navigator.userAgent.includes('CriOS') || navigator.userAgent.includes('FxiOS');
    return FLAG_APPLE_WALLET_PASSES_ENABLED && flags.hasAppleWalletPass && !flags.isChangeInProgress && !iOSChromeOrFirefox;
  };

  const cells = [
    {
      label: 'Check-in',
      value: (
        <Box>
          {reservation.formattedCheckIn} <br />
          <Text fontSize="sm">
            from {reservation.property.checkInAfter}
            <br />
            <ChangeDatesButton booking={booking} />
            <AddToCalendar booking={booking} />
          </Text>
        </Box>
      ),
    },
    {
      label: 'Check-out',
      value: (
        <Box>
          {reservation.formattedCheckOut} <br />
          <Text fontSize="sm">by {reservation.property.checkOutBefore}</Text>
        </Box>
      ),
    },
    {
      label: 'Guests',
      value: (
        <Box>
          <Occupants numberOfAdults={reservation.adults} numberOfChildren={reservation.children} numberOfInfants={reservation.infants} />
        </Box>
      ),
    },
    {
      label: 'Room',
      value: (
        <Box>
          {displayRoomType && (
            <Text fontSize="lg">
              {reservation.roomType.name}
              <br />
            </Text>
          )}

          {displayBedGroup && (
            <Text fontSize={displayRoomType ? 'sm' : 'lg'}>
              Bed type: {reservation.bedGroupDescription}
              <br />
            </Text>
          )}

          <Text fontSize={displayRoomType || displayBedGroup ? 'sm' : 'lg'}>
            {reservation.offer.type === 'classic' && <ClassicIcon />}
            {reservation.offer.name}
            <br />
          </Text>

          <Link to={modal.path} mt={3} fontSize="sm" onClick={track.roomInformationPopUpLinkSelected}>
            View room and offer information
          </Link>
          <RoutableModalWrapper>
            <RoomAndOfferModal booking={booking} {...modal.props} />
          </RoutableModalWrapper>
        </Box>
      ),
    },
    { label: 'Booking reference', value: <Box>{reference}</Box> },
  ];

  if (canDisplayAppleWalletPass(flags)) {
    cells.push({
      label: '',
      value: (
        <Box>
          <DownloadAppleWalletPassButton booking={booking} />
        </Box>
      ),
    });
  }

  return <GridData data={cells} />;
};

export default BookingDetailsCard;
